'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Address {
  id: string;
  type: 'home' | 'work' | 'other';
  name: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  isDefault: boolean;
}

interface AddressManagementSectionProps {
  addresses: Address[];
}

const AddressManagementSection = ({ addresses: initialAddresses }: AddressManagementSectionProps) => {
  const [addresses, setAddresses] = useState<Address[]>(initialAddresses);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleSetDefault = (id: string) => {
    setAddresses(
      addresses.map((addr) => ({
        ...addr,
        isDefault: addr.id === id,
      }))
    );
  };

  const handleDelete = (id: string) => {
    setAddresses(addresses.filter((addr) => addr.id !== id));
  };

  const getAddressTypeIcon = (type: string) => {
    switch (type) {
      case 'home':
        return 'HomeIcon';
      case 'work':
        return 'BuildingOfficeIcon';
      default:
        return 'MapPinIcon';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-secondary/10 rounded-lg">
            <Icon name="MapPinIcon" size={24} className="text-secondary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg text-foreground">Delivery Addresses</h2>
            <p className="text-sm text-muted-foreground">Manage your saved locations</p>
          </div>
        </div>
        <button
          onClick={() => setIsAddingNew(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
        >
          <Icon name="PlusIcon" size={16} />
          <span className="hidden sm:inline">Add Address</span>
        </button>
      </div>

      <div className="space-y-4">
        {addresses.map((address) => (
          <div
            key={address.id}
            className={`p-4 rounded-lg border transition-smooth ${
              address.isDefault
                ? 'border-primary bg-primary/5' :'border-border bg-background hover:border-muted-foreground'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="flex items-center justify-center w-8 h-8 bg-card rounded-md mt-1">
                  <Icon name={getAddressTypeIcon(address.type) as any} size={18} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-sm text-foreground capitalize">{address.type}</h3>
                    {address.isDefault && (
                      <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs font-medium rounded">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-foreground font-medium mb-1">{address.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {address.addressLine1}, {address.addressLine2}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {address.city}, {address.state} - {address.pincode}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Phone: {address.phone}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {!address.isDefault && (
                  <button
                    onClick={() => handleSetDefault(address.id)}
                    className="p-2 rounded-md hover:bg-muted transition-smooth"
                    aria-label="Set as default"
                  >
                    <Icon name="CheckCircleIcon" size={18} className="text-muted-foreground" />
                  </button>
                )}
                <button
                  onClick={() => setEditingId(address.id)}
                  className="p-2 rounded-md hover:bg-muted transition-smooth"
                  aria-label="Edit address"
                >
                  <Icon name="PencilIcon" size={18} className="text-muted-foreground" />
                </button>
                <button
                  onClick={() => handleDelete(address.id)}
                  className="p-2 rounded-md hover:bg-error/10 transition-smooth"
                  aria-label="Delete address"
                >
                  <Icon name="TrashIcon" size={18} className="text-error" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {addresses.length === 0 && (
          <div className="text-center py-12">
            <Icon name="MapPinIcon" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground mb-4">No addresses saved yet</p>
            <button
              onClick={() => setIsAddingNew(true)}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
            >
              Add Your First Address
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressManagementSection;